import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import client from '../api/client';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import ProjectOfficeFilterMultiSelect from '../components/ProjectOfficeFilterMultiSelect';
import SyncedHorizontalScroll from '../components/SyncedHorizontalScroll';
import {
  computeStickyColumnOffsets,
  computeStickyColumnWidths,
  sumStickyColumnWidths,
} from '../utils/stickyColumnLayout';
import { filterProjectsByStatuses } from '../utils/strategicMapStatusFilter';

// Константы для типов вех и их цветов (мягкие пастельные оттенки)
const MILESTONE_TYPES = {
  'РНС': { color: '#8bc49a', bgColor: 'rgba(139, 196, 154, 0.15)', label: 'РНС' },
  'РНВ': { color: '#7ab5ad', bgColor: 'rgba(122, 181, 173, 0.15)', label: 'РНВ' },
  'Продажа': { color: '#7b9eb8', bgColor: 'rgba(123, 158, 184, 0.15)', label: 'Продажа' },
  'Аренда': { color: '#6ba3c9', bgColor: 'rgba(107, 163, 201, 0.15)', label: 'Аренда' },
  'Строительство': { color: '#d4b896', bgColor: 'rgba(212, 184, 150, 0.15)', label: 'Строит.' },
  'Проектирование': { color: '#a99bc4', bgColor: 'rgba(169, 155, 196, 0.15)', label: 'Проект.' },
  'Проектное финансирование': { color: '#b8a8d4', bgColor: 'rgba(184, 168, 212, 0.15)', label: 'Проект. фин.' },
  'Согласование': { color: '#d4a0b8', bgColor: 'rgba(212, 160, 184, 0.15)', label: 'Соглас.' },
  'Завершение': { color: '#8bc49a', bgColor: 'rgba(139, 196, 154, 0.2)', label: 'Заверш.' },
  'Подготовка': { color: '#8b9cc4', bgColor: 'rgba(139, 156, 196, 0.15)', label: 'Подгот.' },
};

// Константы для статусов проектов
const STATUS_OPTIONS = [
  'Строительство',
  'Проектирование, экспертиза, получение РНС',
  'Поиск участка',
  'Приобретение прав на участок',
  'Есть право на зу, ждет реализации',
  'Завершен'
];

// Плановые годы сводных строк мастер-карты (менять здесь при смене стратегии)
const STRATEGY_TARGET_YEAR = 2027;
const ACQUISITION_TARGET_YEAR = 2026;

// Служебные строки мастер-карты (не показываются в основной таблице)
const ACQUISITION_LABEL = `К приобретению ${ACQUISITION_TARGET_YEAR}`;
const STRATEGY_LABEL = `Стратегия ${STRATEGY_TARGET_YEAR}`;
const STRATEGY_PRIOR_LABEL = `Стратегия ${ACQUISITION_TARGET_YEAR}`;
const ACQUISITION_2026_LABEL = ACQUISITION_LABEL;
const STRATEGY_2027_LABEL = STRATEGY_LABEL;
const STRATEGY_2026_PROJECT_NAMES = [ACQUISITION_LABEL, STRATEGY_PRIOR_LABEL];
const STRATEGY_2027_PROJECT_NAMES = [STRATEGY_LABEL];

const STRATEGY_SUMMARY_PROJECT_NAMES = new Set([
  ...STRATEGY_2026_PROJECT_NAMES,
  ...STRATEGY_2027_PROJECT_NAMES,
]);

const PERSPECTIVE_SITES_GROUP = 'Перспективные участки';

const isPerspectiveProject = (project) => (
  normalizeStatus(project.parent_group) === PERSPECTIVE_SITES_GROUP
);

const isRegularDataProject = (project) => (
  !project.is_subtotal && !project.is_total && !isStrategySummaryProject(project)
);

const normalizeStatus = (status) => (status || '').trim();

const isStrategySummaryProject = (project) => (
  STRATEGY_SUMMARY_PROJECT_NAMES.has(project.name.trim())
);

const sumSellableAreaByStatus = (projects, status) => (
  projects
    .filter((project) => (
      !project.is_subtotal
      && !project.is_total
      && !isStrategySummaryProject(project)
      && normalizeStatus(project.current_status) === normalizeStatus(status)
    ))
    .reduce((sum, project) => sum + (project.sellable_area || 0), 0)
);

const findStrategyProject = (projects, names) => {
  for (const name of names) {
    const project = projects.find((item) => item.name.trim() === name);
    if (project) return project;
  }
  return null;
};

const matchCurrentStatus = (projectStatus, expectedStatus) => (
  normalizeStatus(projectStatus) === normalizeStatus(expectedStatus)
);

const compareProjectsByOrder = (a, b) => {
  const orderDiff = (a.order_index ?? 0) - (b.order_index ?? 0);
  if (orderDiff !== 0) return orderDiff;
  const nameDiff = a.name.trim().localeCompare(b.name.trim(), 'ru');
  if (nameDiff !== 0) return nameDiff;
  return (a.id ?? 0) - (b.id ?? 0);
};

// Поиск участка = Стратегия 2027 − Проектирование − Приобретение прав − Есть право на ЗУ
const SEARCH_SITE_SUBTRACT_STATUSES = [
  'Проектирование, экспертиза, получение РНС',
  'Приобретение прав на участок',
  'Есть право на зу, ждет реализации',
];

const getSummaryRowTestId = (row) => {
  if (row.key === STRATEGY_2027_LABEL) return 'strategic-map-total-strategy-2027';
  if (row.key === ACQUISITION_2026_LABEL) return 'strategic-map-total-acquisition-2026';
  if (row.status === 'Поиск участка') return 'strategic-map-total-search-site';
  return undefined;
};

const normalizeMilestoneType = (type) => (
  type === 'Проектное проектирование' ? 'Проектное финансирование' : type
);

const CONSTRUCTION_DURATION_START_TYPE = 'Строительство';
const CONSTRUCTION_DURATION_END_TYPE = 'РНВ';
const CONSTRUCTION_PERIOD_BG = {
  light: 'rgba(59, 130, 246, 0.18)',
  dark: 'rgba(96, 165, 250, 0.22)',
};

const toYearMonthIndex = (monthDate) => {
  const date = new Date(monthDate);
  return date.getFullYear() * 12 + date.getMonth();
};

const toDateYearMonthIndex = (date) => date.getFullYear() * 12 + date.getMonth();

const getConstructionPeriodRange = (milestones) => {
  if (!milestones?.length) return null;

  let constructionStart = null;
  let rnvMonth = null;

  milestones.forEach((milestone) => {
    if (!milestone?.milestone_type || !milestone.month_date) return;
    const monthIndex = toYearMonthIndex(milestone.month_date);
    if (milestone.milestone_type === CONSTRUCTION_DURATION_START_TYPE) {
      if (constructionStart === null || monthIndex < constructionStart) {
        constructionStart = monthIndex;
      }
    }
    if (milestone.milestone_type === CONSTRUCTION_DURATION_END_TYPE) {
      if (rnvMonth === null || monthIndex > rnvMonth) {
        rnvMonth = monthIndex;
      }
    }
  });

  if (constructionStart === null || rnvMonth === null || rnvMonth < constructionStart) {
    return null;
  }

  return { start: constructionStart, end: rnvMonth };
};

const calculateConstructionDurationFromMilestones = (milestones) => {
  const range = getConstructionPeriodRange(milestones);
  if (!range) return null;
  return range.end - range.start + 1;
};

const isDateInConstructionPeriod = (project, date) => {
  if (project.is_subtotal || project.is_total) return false;
  const range = getConstructionPeriodRange(project.milestones);
  if (!range) return false;
  const monthIndex = toDateYearMonthIndex(date);
  return monthIndex >= range.start && monthIndex <= range.end;
};

/** Границы ячейки внутри периода Строительство→РНВ (для замкнутой обводки). */
const getConstructionPeriodEdgeFlags = (project, date) => {
  if (project.is_subtotal || project.is_total) return null;
  const range = getConstructionPeriodRange(project.milestones);
  if (!range) return null;
  const monthIndex = toDateYearMonthIndex(date);
  if (monthIndex < range.start || monthIndex > range.end) return null;
  return {
    isStart: monthIndex === range.start,
    isEnd: monthIndex === range.end,
  };
};

const getProjectConstructionDuration = (project) => {
  if (project.is_subtotal || project.is_total) {
    return project.construction_duration ?? null;
  }
  return calculateConstructionDurationFromMilestones(project.milestones);
};

const StrategicMap = () => {
  const { canEdit } = usePermissions();
  const strategicMapCanEdit = canEdit(PAGE_PERMISSION_IDS.strategic_map);
  const { showSuccess, showError } = useToast();
  const { isDark } = useTheme();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editAreaValue, setEditAreaValue] = useState('');
  const [selectedMilestoneType, setSelectedMilestoneType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());
  const [quickFillType, setQuickFillType] = useState(null); // 'РНС', 'Продажа' etc.
  const [updatingCells, setUpdatingCells] = useState(new Set()); // Набор `${projectId}-${dateStr}`
  const [hoveredRow, setHoveredRow] = useState(null);
  const [startYear, setStartYear] = useState(2022);
  const [endYear, setEndYear] = useState(2028);
  const [yearRangeLimits, setYearRangeLimits] = useState({ min: 2018, max: 2035 });
  const [updatingProjectFields, setUpdatingProjectFields] = useState(new Set()); // `${projectId}-${field}`
  const [projectFieldEdits, setProjectFieldEdits] = useState({});
  const [editingAreaCell, setEditingAreaCell] = useState(null); // { projectId, date, displayDate }
  const [areaEditValue, setAreaEditValue] = useState('');
  const [movingProjectId, setMovingProjectId] = useState(null);
  const [stickyContainerWidth, setStickyContainerWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const tableRef = useRef(null);
  const stickyMeasureRef = useRef(null);

  // Генерация месяцев для временной шкалы (с startYear по endYear)
  const months = useMemo(() => {
    const result = [];
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 0; month < 12; month++) {
        result.push(new Date(year, month, 1));
      }
    }
    return result;
  }, [startYear, endYear]);

  // Фильтруем месяцы по году
  const filteredMonths = useMemo(() => {
    if (yearFilter === 'all') return months;
    const year = parseInt(yearFilter);
    return months.filter(m => m.getFullYear() === year);
  }, [months, yearFilter]);

  // Группировка месяцев по кварталам
  const quarters = useMemo(() => {
    const grouped = {};
    filteredMonths.forEach(date => {
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const key = `${quarter} кв. ${year}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(date);
    });
    return grouped;
  }, [filteredMonths]);

  const yearsInView = useMemo(() => {
    const years = [];
    filteredMonths.forEach(date => {
      const year = date.getFullYear();
      if (!years.includes(year)) years.push(year);
    });
    return years;
  }, [filteredMonths]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const node = stickyMeasureRef.current;
    if (!node) return undefined;
    const update = () => {
      const width = node.clientWidth || window.innerWidth || 1200;
      setStickyContainerWidth((prev) => (prev === width ? prev : width));
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [loading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Сначала пытаемся синхронизировать проекты из объектов строительства
      // Если синхронизация не удалась, просто продолжаем загрузку данных
      try {
        await client.post('/strategic-map/sync-from-cities');
      } catch (syncError) {
        console.warn('Синхронизация проектов не удалась, продолжаем загрузку:', syncError);
        // Не показываем ошибку пользователю, просто продолжаем
      }
      
      // Затем загружаем проекты
      const projectsRes = await client.get('/strategic-map/projects');
      setProjects(projectsRes.data);
    } catch (e) {
      console.error(e);
      showError('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  // Вспомогательная функция для форматирования даты без сдвига часового пояса
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Получить список вех для проекта на конкретный месяц
  const getMilestones = useCallback((project, date) => {
    if (!project.milestones) return null;
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return project.milestones.filter(m => {
      const mDate = new Date(m.month_date);
      const mYearMonth = `${mDate.getFullYear()}-${String(mDate.getMonth() + 1).padStart(2, '0')}`;
      return mYearMonth === yearMonth;
    });
  }, []);

  const findMilestoneByType = useCallback((project, date, milestoneType) => {
    const milestones = getMilestones(project, date) || [];
    return milestones.find(m => normalizeMilestoneType(m.milestone_type) === normalizeMilestoneType(milestoneType));
  }, [getMilestones]);

  const getCellAreaMilestone = useCallback((project, date) => {
    const milestones = getMilestones(project, date) || [];
    return milestones.find(m => m.area_value !== null && m.area_value !== undefined) || null;
  }, [getMilestones]);

  const getCellAreaValue = useCallback((project, date) => {
    const carrier = getCellAreaMilestone(project, date);
    return carrier ? carrier.area_value : null;
  }, [getCellAreaMilestone]);

  const persistConstructionDuration = useCallback(async (projectId, milestones) => {
    const duration = calculateConstructionDurationFromMilestones(milestones);
    try {
      await client.put(`/strategic-map/projects/${projectId}`, { construction_duration: duration });
    } catch (e) {
      console.error(e);
    }
    return duration;
  }, []);

  const applyMilestoneUpdate = useCallback(async (projectId, updatedMilestones) => {
    const duration = await persistConstructionDuration(projectId, updatedMilestones);
    setProjects((prev) => prev.map((project) => (
      project.id === projectId
        ? { ...project, milestones: updatedMilestones, construction_duration: duration }
        : project
    )));
  }, [persistConstructionDuration]);

  const getYearAreaTotal = useCallback((project, year) => {
    if (!project) return 0;
    return filteredMonths.reduce((sum, date) => {
      if (date.getFullYear() !== year) return sum;
      const area = getCellAreaValue(project, date);
      return sum + (area ?? 0);
    }, 0);
  }, [filteredMonths, getCellAreaValue]);

  // Форматирование месяца
  const formatMonth = (date) => {
    const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
    return months[date.getMonth()];
  };

  // Обработка клика по ячейке для редактирования
  const handleCellClick = async (projectId, date) => {
    if (!strategicMapCanEdit) return;
    const dateStr = formatDateLocal(date);
    const cellKey = `${projectId}-${dateStr}`;
    
    if (updatingCells.has(cellKey)) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const milestonesForMonth = getMilestones(project, date) || [];
    const legendMilestones = milestonesForMonth.filter(m => m.milestone_type);
    const primaryMilestone = legendMilestones[0] || null;
    const cellAreaValue = getCellAreaValue(project, date);

    if (editingAreaCell) {
      if (editingAreaCell.projectId === projectId && editingAreaCell.date === dateStr) {
        return;
      }
      await saveAreaOnlyEdit();
    }

    if (quickFillType) {
      // Режим быстрого заполнения
      setUpdatingCells(prev => new Set(prev).add(cellKey));
      try {
        let updatedMilestones = [...(project.milestones || [])];
        const existingMilestone = findMilestoneByType(project, date, quickFillType);
        if (existingMilestone) {
          const remainingMilestones = milestonesForMonth.filter(m => m.id !== existingMilestone.id);
          const areaToPreserve = existingMilestone.area_value ?? null;

          await client.delete(`/strategic-map/milestones/${existingMilestone.id}`);
          updatedMilestones = updatedMilestones.filter(m => m.id !== existingMilestone.id);

          if (areaToPreserve !== null) {
            if (remainingMilestones.length > 0) {
              const carrier = remainingMilestones[0];
              await client.put(`/strategic-map/milestones/${carrier.id}`, { area_value: areaToPreserve });
              updatedMilestones = updatedMilestones.map(m => (
                m.id === carrier.id
                  ? { ...m, area_value: areaToPreserve }
                  : { ...m, area_value: m.area_value ?? null }
              ));
            } else {
              const createdArea = await client.post(
                `/strategic-map/projects/${projectId}/milestones`,
                {
                  month_date: dateStr,
                  milestone_type: null,
                  value: null,
                  area_value: areaToPreserve,
                  is_key_milestone: false
                }
              );
              updatedMilestones = [...updatedMilestones, createdArea.data];
            }
          }
        } else {
          const created = await client.post(
            `/strategic-map/projects/${projectId}/milestones`,
            {
              month_date: dateStr,
              milestone_type: quickFillType,
              value: null,
              area_value: null,
              is_key_milestone: ['РНС', 'Завершение'].includes(quickFillType)
            }
          );
          updatedMilestones = [...updatedMilestones, created.data];
        }

        await applyMilestoneUpdate(projectId, updatedMilestones);
      } catch (e) {
        console.error(e);
        showError('Ошибка при обновлении');
        await fetchData();
      } finally {
        setUpdatingCells(prev => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }
      return;
    }

    setEditingCell({ projectId, date: dateStr, displayDate: date });
    setEditValue(primaryMilestone?.value || '');
    setSelectedMilestoneType(normalizeMilestoneType(primaryMilestone?.milestone_type || ''));
    setEditAreaValue(cellAreaValue != null ? String(cellAreaValue) : '');
  };

  // Закрытие модального окна редактирования
  const closeEditModal = () => {
    setEditingCell(null);
    setEditValue('');
    setEditAreaValue('');
    setSelectedMilestoneType('');
  };

  const closeAreaEdit = () => {
    setEditingAreaCell(null);
    setAreaEditValue('');
  };

  useEffect(() => {
    if (!editingCell && !editingAreaCell) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (editingCell) closeEditModal();
        if (editingAreaCell) closeAreaEdit();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editingCell, editingAreaCell]);

  // Сохранение редактирования ячейки
  const saveCellEdit = async () => {
    if (!editingCell) return;
    
    try {
      const project = projects.find(p => p.id === editingCell.projectId);
      if (!project) {
        showError('Проект не найден');
        closeEditModal();
        return;
      }
      const displayDate = editingCell.displayDate || new Date(editingCell.date);
      const monthMilestones = getMilestones(project, displayDate) || [];
      const rawArea = (editAreaValue ?? '').toString();
      const trimmedArea = rawArea.trim();
      const parsedAreaValue = trimmedArea === '' ? null : parseFloat(trimmedArea);
      if (parsedAreaValue !== null && Number.isNaN(parsedAreaValue)) {
        showError('Неверный формат числа');
        return;
      }
      const areaCarrier = monthMilestones.find(m => m.area_value !== null && m.area_value !== undefined) || null;

      let updatedMilestones = [...(project.milestones || [])];

      if (!selectedMilestoneType) {
        if (parsedAreaValue === null) {
          // Если тип не выбран и площадь пустая — очищаем все вехи в этой ячейке
          await Promise.all(monthMilestones.map(m => client.delete(`/strategic-map/milestones/${m.id}`)));
          updatedMilestones = updatedMilestones.filter(m => !monthMilestones.find(mm => mm.id === m.id));
        } else {
          // Тип не выбран, но площадь задана — сохраняем площадь на уровне ячейки
          let carrier = areaCarrier;
          if (!carrier && monthMilestones.length > 0) {
            carrier = monthMilestones[0];
          }
          if (carrier) {
            await client.put(`/strategic-map/milestones/${carrier.id}`, { area_value: parsedAreaValue });
            updatedMilestones = updatedMilestones.map(m => 
              m.id === carrier.id ? { ...m, area_value: parsedAreaValue } : m
            );
          } else {
            const response = await client.post(
              `/strategic-map/projects/${editingCell.projectId}/milestones`,
              {
                month_date: editingCell.date,
                milestone_type: null,
                value: null,
                area_value: parsedAreaValue,
                is_key_milestone: false
              }
            );
            updatedMilestones.push(response.data);
          }
          const toClear = monthMilestones.filter(m => carrier && m.id !== carrier.id && m.area_value !== null && m.area_value !== undefined);
          if (toClear.length > 0) {
            await Promise.all(toClear.map(m => client.put(`/strategic-map/milestones/${m.id}`, { area_value: null })));
            updatedMilestones = updatedMilestones.map(m => 
              toClear.find(tc => tc.id === m.id) ? { ...m, area_value: null } : m
            );
          }
        }
      } else {
        let selectedMilestone = monthMilestones.find(m => (
          normalizeMilestoneType(m.milestone_type) === normalizeMilestoneType(selectedMilestoneType)
        ));
        const milestoneTypeToSave = normalizeMilestoneType(selectedMilestoneType);
        if (selectedMilestone) {
          await client.put(
            `/strategic-map/milestones/${selectedMilestone.id}`,
            {
              value: editValue || null,
              milestone_type: milestoneTypeToSave,
              is_key_milestone: ['РНС', 'Завершение'].includes(milestoneTypeToSave)
            }
          );
          updatedMilestones = updatedMilestones.map(m => 
            m.id === selectedMilestone.id 
              ? { ...m, value: editValue || null, milestone_type: milestoneTypeToSave, is_key_milestone: ['РНС', 'Завершение'].includes(milestoneTypeToSave) }
              : m
          );
        } else {
          const created = await client.post(
            `/strategic-map/projects/${editingCell.projectId}/milestones`,
            {
              month_date: editingCell.date,
              milestone_type: milestoneTypeToSave,
              value: editValue || null,
              area_value: null,
              is_key_milestone: ['РНС', 'Завершение'].includes(milestoneTypeToSave)
            }
          );
          selectedMilestone = created.data;
          updatedMilestones.push(selectedMilestone);
        }

        if (parsedAreaValue === null) {
          if (areaCarrier) {
            await client.put(`/strategic-map/milestones/${areaCarrier.id}`, { area_value: null });
            updatedMilestones = updatedMilestones.map(m => 
              m.id === areaCarrier.id ? { ...m, area_value: null } : m
            );
          }
        } else {
          const carrier = areaCarrier || selectedMilestone;
          if (carrier) {
            await client.put(`/strategic-map/milestones/${carrier.id}`, { area_value: parsedAreaValue });
            updatedMilestones = updatedMilestones.map(m => 
              m.id === carrier.id ? { ...m, area_value: parsedAreaValue } : m
            );
            const toClear = monthMilestones.filter(m => m.id !== carrier.id && m.area_value !== null && m.area_value !== undefined);
            if (toClear.length > 0) {
              await Promise.all(toClear.map(m => client.put(`/strategic-map/milestones/${m.id}`, { area_value: null })));
              updatedMilestones = updatedMilestones.map(m => 
                toClear.find(tc => tc.id === m.id) ? { ...m, area_value: null } : m
              );
            }
          }
        }
      }

      await applyMilestoneUpdate(editingCell.projectId, updatedMilestones);

      showSuccess('Сохранено');
    } catch (e) {
      console.error(e);
      showError('Ошибка сохранения');
      // В случае ошибки перезагружаем данные
      await fetchData();
    }
    closeEditModal();
  };

  const saveAreaOnlyEdit = async () => {
    if (!editingAreaCell) return;

    const rawArea = (areaEditValue ?? '').toString();
    const trimmedArea = rawArea.trim();
    const parsedAreaValue = trimmedArea === '' ? null : parseFloat(trimmedArea);
    if (parsedAreaValue !== null && Number.isNaN(parsedAreaValue)) {
      showError('Неверный формат числа');
      return;
    }

    try {
      const project = projects.find(p => p.id === editingAreaCell.projectId);
      if (!project) {
        showError('Проект не найден');
        closeAreaEdit();
        return;
      }
      const displayDate = editingAreaCell.displayDate || new Date(editingAreaCell.date);
      const monthMilestones = getMilestones(project, displayDate) || [];
      const areaCarrier = monthMilestones.find(m => m.area_value !== null && m.area_value !== undefined) || null;

      let updatedMilestones = [...(project.milestones || [])];

      if (parsedAreaValue === null) {
        if (areaCarrier) {
          await client.put(`/strategic-map/milestones/${areaCarrier.id}`, { area_value: null });
          updatedMilestones = updatedMilestones.map(m => 
            m.id === areaCarrier.id ? { ...m, area_value: null } : m
          );
        }
      } else {
        let carrier = areaCarrier;
        if (!carrier && monthMilestones.length > 0) {
          carrier = monthMilestones[0];
        }
        if (carrier) {
          await client.put(`/strategic-map/milestones/${carrier.id}`, { area_value: parsedAreaValue });
          updatedMilestones = updatedMilestones.map(m => 
            m.id === carrier.id ? { ...m, area_value: parsedAreaValue } : m
          );
        } else {
          const response = await client.post(
            `/strategic-map/projects/${editingAreaCell.projectId}/milestones`,
            {
              month_date: editingAreaCell.date,
              milestone_type: null,
              value: null,
              area_value: parsedAreaValue,
              is_key_milestone: false
            }
          );
          updatedMilestones.push(response.data);
        }
        const toClear = monthMilestones.filter(m => carrier && m.id !== carrier.id && m.area_value !== null && m.area_value !== undefined);
        if (toClear.length > 0) {
          await Promise.all(toClear.map(m => client.put(`/strategic-map/milestones/${m.id}`, { area_value: null })));
          updatedMilestones = updatedMilestones.map(m => 
            toClear.find(tc => tc.id === m.id) ? { ...m, area_value: null } : m
          );
        }
      }

      // Обновляем локальное состояние вместо перезагрузки
      setProjects(prev => prev.map(p => 
        p.id === editingAreaCell.projectId 
          ? { ...p, milestones: updatedMilestones }
          : p
      ));

      showSuccess('Сохранено');
    } catch (e) {
      console.error(e);
      showError('Ошибка сохранения');
      // В случае ошибки перезагружаем данные
      await fetchData();
    }

    closeAreaEdit();
  };

  const deleteLegendMilestone = async (project, date, milestone) => {
    if (!milestone?.id) return;
    const dateStr = formatDateLocal(date);
    const cellKey = `${project.id}-${dateStr}`;
    if (updatingCells.has(cellKey)) return;

    setUpdatingCells(prev => new Set(prev).add(cellKey));
    try {
      const allMilestones = getMilestones(project, date) || [];
      const remaining = allMilestones.filter(x => x.id !== milestone.id);
      const areaToPreserve = milestone.area_value ?? null;

      let updatedMilestones = [...(project.milestones || [])];

      await client.delete(`/strategic-map/milestones/${milestone.id}`);
      updatedMilestones = updatedMilestones.filter(m => m.id !== milestone.id);

      if (areaToPreserve !== null) {
        if (remaining.length > 0) {
          const carrier = remaining[0];
          await client.put(`/strategic-map/milestones/${carrier.id}`, { area_value: areaToPreserve });
          updatedMilestones = updatedMilestones.map(m => 
            m.id === carrier.id ? { ...m, area_value: areaToPreserve } : m
          );
        } else {
          const response = await client.post(
            `/strategic-map/projects/${project.id}/milestones`,
            {
              month_date: dateStr,
              milestone_type: null,
              value: null,
              area_value: areaToPreserve,
              is_key_milestone: false
            }
          );
          updatedMilestones.push(response.data);
        }
      }

      await applyMilestoneUpdate(project.id, updatedMilestones);
    } catch (e) {
      console.error(e);
      showError('Ошибка удаления');
      // В случае ошибки перезагружаем данные
      await fetchData();
    } finally {
      setUpdatingCells(prev => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
    }
  };


  // Фильтрация проектов
  const filteredProjects = useMemo(() => {
    if (!searchText.trim()) return projects;
    const q = searchText.toLowerCase();
    
    // Сначала находим все подходящие проекты (не метрики)
    const matchingProjectIds = new Set(
      projects
        .filter(p => !p.parent_id && p.name.toLowerCase().includes(q))
        .map(p => p.id)
    );

    return projects.filter(p => {
      // Показываем если:
      // 1. Само имя совпадает
      // 2. Это метрика и её родитель совпадает
      // 3. Группа совпадает
      return (
        p.name.toLowerCase().includes(q) ||
        (p.parent_id && matchingProjectIds.has(p.parent_id)) ||
        (p.parent_group && p.parent_group.toLowerCase().includes(q))
      );
    });
  }, [projects, searchText]);

  const visibleProjects = useMemo(() => {
    const base = filteredProjects.filter((project) => (
      !isStrategySummaryProject(project) && !project.is_city_hidden
    ));
    return filterProjectsByStatuses(base, selectedStatuses);
  }, [filteredProjects, selectedStatuses]);

  const mainPoolProjects = useMemo(() => (
    visibleProjects
      .filter((project) => !isPerspectiveProject(project))
      .slice()
      .sort(compareProjectsByOrder)
  ), [visibleProjects]);

  const perspectivePoolProjects = useMemo(() => (
    visibleProjects
      .filter((project) => isPerspectiveProject(project))
      .slice()
      .sort(compareProjectsByOrder)
  ), [visibleProjects]);

  // Все обычные проекты, включая скрытые города — для нижнего блока ИТОГО
  const dataProjects = useMemo(() => (
    projects.filter((project) => isRegularDataProject(project))
  ), [projects]);

  // Строки «Итого в строительстве…» — видимые в таблице (без скрытых + status filter)
  const constructionProjects = useMemo(() => {
    const visibleConstruction = dataProjects.filter((project) => (
      !project.is_city_hidden
      && matchCurrentStatus(project.current_status, 'Строительство')
    ));
    return filterProjectsByStatuses(visibleConstruction, selectedStatuses);
  }, [dataProjects, selectedStatuses]);

  const summaryTotals = useMemo(() => {
    const byStatus = {};

    STATUS_OPTIONS.forEach((status) => {
      if (status !== 'Поиск участка') {
        byStatus[status] = sumSellableAreaByStatus(dataProjects, status);
      }
    });

    const strategy2027Project = findStrategyProject(projects, STRATEGY_2027_PROJECT_NAMES);
    const strategy2027 = strategy2027Project?.sellable_area ?? 0;

    byStatus['Поиск участка'] = SEARCH_SITE_SUBTRACT_STATUSES.reduce(
      (remaining, status) => remaining - (byStatus[status] || 0),
      strategy2027
    );

    // К приобретению 2026 = Поиск участка + Приобретение прав на участок
    const acquisition2026 = (byStatus['Поиск участка'] || 0)
      + (byStatus['Приобретение прав на участок'] || 0);

    return {
      byStatus,
      acquisition2026,
      strategy2027,
      strategy2027Project,
    };
  }, [dataProjects, projects]);

  const summaryTableRows = useMemo(() => ([
    ...STATUS_OPTIONS.map((status) => ({ key: status, label: status, type: 'status', status })),
    {
      key: ACQUISITION_2026_LABEL,
      label: ACQUISITION_2026_LABEL,
      type: 'computed',
      value: summaryTotals.acquisition2026,
    },
    {
      key: STRATEGY_2027_LABEL,
      label: STRATEGY_2027_LABEL,
      type: 'editable',
      projectNames: STRATEGY_2027_PROJECT_NAMES,
      createName: STRATEGY_2027_LABEL,
      value: summaryTotals.strategy2027,
      project: summaryTotals.strategy2027Project,
    },
  ]), [summaryTotals]);

  const getSummaryRowBgColor = (label) => {
    if (label === 'Завершен') return '#fef08a';
    if (label === 'Строительство') return '#fecaca';
    if (label === 'Проектирование, экспертиза, получение РНС') return '#fef3c7';
    if (label === ACQUISITION_2026_LABEL) return '#dbeafe';
    if (label === STRATEGY_2027_LABEL) return '#e9d5ff';
    return 'transparent';
  };

  // Получить уникальные годы для фильтра
  const availableYears = useMemo(() => {
    const years = new Set();
    for (let y = startYear; y <= endYear; y++) years.add(y);
    return Array.from(years);
  }, [startYear, endYear]);

  // Добавить год в начало
  const addYearBefore = () => {
    setStartYear(prev => {
      const next = prev - 1;
      setYearRangeLimits(limit => ({ ...limit, min: Math.min(limit.min, next) }));
      return next;
    });
  };

  // Добавить год в конец
  const addYearAfter = () => {
    setEndYear(prev => {
      const next = prev + 1;
      setYearRangeLimits(limit => ({ ...limit, max: Math.max(limit.max, next) }));
      return next;
    });
  };

  const moveProjectPool = async (projectId, toPerspective) => {
    if (movingProjectId) return;

    setMovingProjectId(projectId);
    const parent_group = toPerspective ? PERSPECTIVE_SITES_GROUP : null;
    setProjects((prev) => prev.map((project) => (
      project.id === projectId ? { ...project, parent_group } : project
    )));

    try {
      await client.put(`/strategic-map/projects/${projectId}`, { parent_group });
      showSuccess(toPerspective ? 'Участок перенесён в перспективные' : 'Участок возвращён в общий пул');
    } catch (e) {
      console.error(e);
      showError('Ошибка при переносе участка');
      await fetchData();
    } finally {
      setMovingProjectId(null);
    }
  };

  const updateProjectField = async (projectId, field, value) => {
    const fieldKey = `${projectId}-${field}`;
    if (updatingProjectFields.has(fieldKey)) return;

    setUpdatingProjectFields(prev => new Set(prev).add(fieldKey));
    setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, [field]: value } : p)));

    try {
      await client.put(`/strategic-map/projects/${projectId}`, { [field]: value });
    } catch (e) {
      console.error(e);
      showError('Ошибка сохранения');
      fetchData();
    } finally {
      setUpdatingProjectFields(prev => {
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
    }
  };

  const getFieldKey = (projectId, field) => `${projectId}-${field}`;

  const setFieldEditValue = (projectId, field, value) => {
    const key = getFieldKey(projectId, field);
    setProjectFieldEdits(prev => ({ ...prev, [key]: value }));
  };

  const clearFieldEditValue = (projectId, field) => {
    const key = getFieldKey(projectId, field);
    setProjectFieldEdits(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getDisplayValue = (value) => (value === null || value === undefined ? '' : String(value));

  const commitNumericField = (projectId, field, rawValue, parser = 'int') => {
    const trimmed = rawValue.trim();
    const parsed = trimmed === '' ? null : (parser === 'float' ? parseFloat(trimmed) : parseInt(trimmed, 10));
    if (parsed !== null && Number.isNaN(parsed)) {
      showError('Неверный формат числа');
      return;
    }
    updateProjectField(projectId, field, parsed);
  };

  const commitTextField = (projectId, field, rawValue) => {
    const trimmed = rawValue.trim();
    updateProjectField(projectId, field, trimmed === '' ? null : trimmed);
  };

  const getNormalizedStatusValue = (status) => {
    const normalized = normalizeStatus(status);
    if (!normalized) return '';
    return STATUS_OPTIONS.find((option) => normalizeStatus(option) === normalized) || normalized;
  };

  const formatSummaryDisplayValue = (row, total) => {
    if (total === null || total === undefined) return '—';
    if (row.status === 'Поиск участка' || row.type === 'computed') {
      return total.toLocaleString('ru-RU');
    }
    return total !== 0 ? total.toLocaleString('ru-RU') : '—';
  };

  const getStrategySummaryFieldKey = (rowKey) => `strategy-summary-${rowKey}`;

  const setSummaryEditValue = (rowKey, value) => {
    const key = getStrategySummaryFieldKey(rowKey);
    setProjectFieldEdits((prev) => ({ ...prev, [key]: value }));
  };

  const clearSummaryEditValue = (rowKey) => {
    const key = getStrategySummaryFieldKey(rowKey);
    setProjectFieldEdits((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const commitStrategySummaryValue = async (row, rawValue) => {
    if (!strategicMapCanEdit) return;

    const trimmed = rawValue.trim();
    const parsed = trimmed === '' ? null : parseFloat(trimmed);
    if (parsed !== null && Number.isNaN(parsed)) {
      showError('Неверный формат числа');
      return;
    }

    const existingProject = row.project || findStrategyProject(projects, row.projectNames);

    try {
      if (existingProject) {
        await updateProjectField(existingProject.id, 'sellable_area', parsed);
      } else {
        const response = await client.post('/strategic-map/projects', {
          name: row.createName,
          sellable_area: parsed,
          is_subtotal: true,
          order_index: projects.length + 1,
        });
        setProjects((prev) => [...prev, response.data]);
      }
    } catch (e) {
      console.error(e);
      showError('Ошибка сохранения');
      fetchData();
    }
  };

  // Стили
  const containerStyle = {
    padding: '0 40px',
    paddingBottom: 40,
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16
  };

  const titleStyle = {
    fontSize: 28,
    fontWeight: 700,
    background: isDark 
      ? 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)'
      : 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0
  };

  const toolbarStyle = {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 20,
    padding: '16px 20px',
    background: isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.9)',
    borderRadius: 12,
    border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(203, 213, 225, 0.8)'}`,
    backdropFilter: 'blur(8px)'
  };

  const yearRangeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: isDark ? 'rgba(51, 65, 85, 0.5)' : 'rgba(226, 232, 240, 0.5)',
    borderRadius: 10,
    padding: '8px 10px'
  };

  const yearStepButtonStyle = {
    border: 'none',
    background: isDark ? 'rgba(30, 41, 59, 0.8)' : '#fff',
    color: 'var(--text-primary)',
    borderRadius: 8,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600
  };

  const inlineInputStyle = {
    width: '100%',
    height: 30,
    borderRadius: 6,
    border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(203, 213, 225, 0.9)'}`,
    background: isDark ? 'rgba(15, 23, 42, 0.6)' : '#fff',
    color: 'var(--text-primary)',
    padding: '4px 6px',
    fontSize: 11,
    textAlign: 'center'
  };

  const tableContainerStyle = {
    overflow: 'auto',
    background: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.4)' : 'rgba(203, 213, 225, 0.6)'}`,
    boxShadow: isDark 
      ? '0 4px 24px rgba(0, 0, 0, 0.3)' 
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  };

  const fixedColumnWidths = useMemo(
    () => computeStickyColumnWidths(stickyContainerWidth),
    [stickyContainerWidth]
  );

  const headerRowHeights = {
    top: 48,
    top2: 44,
    top3: 36
  };

  const fixedColumnOffsets = useMemo(
    () => computeStickyColumnOffsets(fixedColumnWidths),
    [fixedColumnWidths]
  );

  const cellStyle = (isHeader = false, isFixed = false, fixedLeft = 0, isFixedVertical = false, fixedWidth = null) => {
    let topValue = 'auto';
    let minHeight = undefined;
    if (isFixedVertical && isHeader) {
      if (isFixedVertical === 'top2') {
        topValue = headerRowHeights.top;
        minHeight = headerRowHeights.top2;
      } else if (isFixedVertical === 'top3') {
        topValue = headerRowHeights.top + headerRowHeights.top2;
        minHeight = headerRowHeights.top3;
      } else {
        topValue = 0;
        minHeight = headerRowHeights.top;
      }
    }
    
    return {
      padding: isHeader ? '10px 8px' : '8px 6px',
      fontSize: isHeader ? 11 : 12,
      fontWeight: isHeader ? 600 : 400,
      textAlign: 'center',
      whiteSpace: isHeader ? 'normal' : 'nowrap',
      wordBreak: isHeader ? 'break-word' : 'normal',
      lineHeight: isHeader ? 1.2 : 'normal',
      verticalAlign: 'middle',
      borderRight: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
      borderBottom: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
      background: isFixed || isFixedVertical
        ? (isDark ? '#1e293b' : '#f8fafc')
        : (isDark ? 'rgba(15, 23, 42, 0.1)' : 'transparent'),
      position: (isFixed || isFixedVertical) ? 'sticky' : 'static',
      left: isFixed ? fixedLeft : 'auto',
      top: isFixedVertical ? topValue : 'auto',
      zIndex: isFixed && isFixedVertical ? 30 : (isFixed ? 20 : (isFixedVertical ? 15 : 1)),
      width: isFixed ? fixedWidth : 'auto',
      minWidth: isFixed ? fixedWidth : (isHeader ? 60 : 80),
      maxWidth: isFixed ? fixedWidth : 'none',
      ...(minHeight ? { minHeight } : {}),
    };
  };

  const yearTotalColumnStyle = (isHeader = false, isFixedVertical = false) => ({
    ...cellStyle(isHeader, false, 0, isFixedVertical),
    minWidth: 80,
    maxWidth: 100,
    background: isDark
      ? 'rgba(30, 41, 59, 0.7)'
      : 'rgba(226, 232, 240, 0.7)',
    fontWeight: isHeader ? 700 : 600
  });

  const getMilestoneCellStyle = (milestone) => {
    if (!milestone || !milestone.value) return {};
    
    const type = MILESTONE_TYPES[normalizeMilestoneType(milestone.milestone_type)] || { color: '#64748b', bgColor: 'rgba(100, 116, 139, 0.1)' };
    
    return {
      background: type.bgColor,
      color: type.color,
      fontWeight: 600,
      borderRadius: 4,
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    };
  };

  const fixedColumnTotalWidth = useMemo(
    () => sumStickyColumnWidths(fixedColumnWidths),
    [fixedColumnWidths]
  );
  const tableColSpan = 7 + filteredMonths.length + yearsInView.length;

  const renderPoolMoveButton = (project, pool) => {
    if (!isRegularDataProject(project) || !strategicMapCanEdit) return null;
    const toPerspective = pool === 'main';
    const isMoving = movingProjectId === project.id;
    return (
      <button
        type="button"
        data-testid={toPerspective ? `strategic-map-move-to-perspective-${project.id}` : `strategic-map-move-to-main-${project.id}`}
        disabled={isMoving}
        onClick={() => moveProjectPool(project.id, toPerspective)}
        title={toPerspective ? 'Перенести в перспективные участки' : 'Вернуть в общий пул'}
        style={{
          fontSize: 9,
          padding: '3px 5px',
          borderRadius: 5,
          border: `1px solid ${isDark ? 'rgba(96, 165, 250, 0.4)' : 'rgba(59, 130, 246, 0.35)'}`,
          background: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)',
          color: isDark ? '#93c5fd' : '#2563eb',
          cursor: isMoving ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
          opacity: isMoving ? 0.6 : 1,
          width: 52,
          lineHeight: 1.15,
        }}
      >
        {isMoving ? '…' : (toPerspective ? '→ Персп.' : '← Общий')}
      </button>
    );
  };

  const renderProjectRow = (project, rowIdx, pool = 'main') => {
    const constructionBorder = isDark ? 'rgba(96, 165, 250, 0.85)' : 'rgba(37, 99, 235, 0.65)';
    return (
    <tr 
      key={`${pool}-${project.id}`}
      data-testid={`strategic-map-project-row-${pool}-${project.id}`}
      data-project-id={project.id}
      data-project-status={normalizeStatus(project.current_status)}
      data-pool={pool}
      data-construction-period={getConstructionPeriodRange(project.milestones) ? 'true' : 'false'}
      onMouseEnter={() => setHoveredRow(project.id)}
      onMouseLeave={() => setHoveredRow(null)}
      style={{ 
        background: project.id === hoveredRow
          ? (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)')
          : pool === 'perspective'
            ? (isDark ? 'rgba(96, 165, 250, 0.08)' : 'rgba(59, 130, 246, 0.05)')
          : project.is_subtotal 
            ? (isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)')
            : project.is_total
              ? (isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)')
              : 'transparent',
        transition: 'background 0.1s',
      }}
    >
      {/* № */}
      <td style={{ 
        ...cellStyle(false, true, fixedColumnOffsets.index, false, fixedColumnWidths.index),
        fontSize: 11,
        fontWeight: project.is_subtotal || project.is_total ? 700 : 400
      }}>
        {project.is_subtotal || project.is_total || !strategicMapCanEdit ? (
          project.order_index ?? rowIdx + 1
        ) : (
          <input
            type="number"
            data-testid={`strategic-map-order-input-${project.id}`}
            value={projectFieldEdits[getFieldKey(project.id, 'order_index')] ?? getDisplayValue(project.order_index ?? rowIdx + 1)}
            onChange={(e) => setFieldEditValue(project.id, 'order_index', e.target.value)}
            onBlur={(e) => {
              commitNumericField(project.id, 'order_index', e.target.value, 'int');
              clearFieldEditValue(project.id, 'order_index');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                clearFieldEditValue(project.id, 'order_index');
                e.currentTarget.blur();
              }
            }}
            title="Номер строки"
            style={{
              ...inlineInputStyle,
              height: 26,
              padding: '2px 3px',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        )}
      </td>
      
      {/* Название проекта */}
      <td style={{ 
        ...cellStyle(false, true, fixedColumnOffsets.project, false, fixedColumnWidths.project),
        textAlign: 'left',
        paddingLeft: project.name.startsWith('  ') ? 8 : 4,
        fontWeight: project.is_subtotal || project.is_total ? 700 : 500,
        fontSize: project.is_subtotal ? 14 : 13,
        color: project.is_subtotal 
          ? '#7b9eb8' 
          : project.is_total 
            ? '#8bc49a'
            : 'var(--text-primary)',
        borderLeft: project.is_subtotal ? '4px solid #7b9eb8' : 'none',
        whiteSpace: 'normal',
        verticalAlign: 'top',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {project.city_id && !project.name.startsWith('  ') && (
            <span style={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              background: '#8bc49a',
              flexShrink: 0,
              marginTop: 5
            }} title="Привязан к объекту" />
          )}
          <span style={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            lineHeight: 1.25
          }}>
            {project.name.trim()}
          </span>
        </div>
      </td>
      
      {/* Текущий статус */}
      <td style={{ 
        ...cellStyle(false, true, fixedColumnOffsets.status, false, fixedColumnWidths.status), 
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        verticalAlign: 'middle',
        color: 'var(--text-primary)',
      }}>
        {project.is_subtotal || project.is_total || !strategicMapCanEdit ? (
          <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', fontWeight: 700, fontSize: 12 }}>
            {getNormalizedStatusValue(project.current_status) || '—'}
          </div>
        ) : (
          <select
            value={projectFieldEdits[getFieldKey(project.id, 'current_status')] ?? getNormalizedStatusValue(project.current_status)}
            onChange={(e) => {
              const newValue = e.target.value;
              setFieldEditValue(project.id, 'current_status', newValue);
              commitTextField(project.id, 'current_status', newValue);
              clearFieldEditValue(project.id, 'current_status');
            }}
            style={{
              ...inlineInputStyle,
              cursor: 'pointer',
              paddingRight: 20,
              height: 'auto',
              minHeight: 60,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              lineHeight: '1.4',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            <option value="">—</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        )}
      </td>
      
      {/* Кол-во секций */}
      <td style={{ ...cellStyle(false, true, fixedColumnOffsets.sections, false, fixedColumnWidths.sections), fontSize: 11 }}>
        {project.is_subtotal || project.is_total || !strategicMapCanEdit ? (
          project.sections_count ?? '—'
        ) : (
          <input
            type="number"
            value={projectFieldEdits[getFieldKey(project.id, 'sections_count')] ?? getDisplayValue(project.sections_count)}
            onChange={(e) => setFieldEditValue(project.id, 'sections_count', e.target.value)}
            onBlur={(e) => {
              commitNumericField(project.id, 'sections_count', e.target.value, 'int');
              clearFieldEditValue(project.id, 'sections_count');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                clearFieldEditValue(project.id, 'sections_count');
                e.currentTarget.blur();
              }
            }}
            style={inlineInputStyle}
          />
        )}
      </td>
      
      {/* Продаваемая площадь (М2) */}
      <td style={{ ...cellStyle(false, true, fixedColumnOffsets.area, false, fixedColumnWidths.area), fontSize: 11 }}>
        {project.is_subtotal || project.is_total || !strategicMapCanEdit ? (
          project.sellable_area != null ? project.sellable_area.toLocaleString('ru-RU') : '—'
        ) : (
          <input
            type="number"
            step="0.01"
            value={projectFieldEdits[getFieldKey(project.id, 'sellable_area')] ?? getDisplayValue(project.sellable_area)}
            onChange={(e) => setFieldEditValue(project.id, 'sellable_area', e.target.value)}
            onBlur={(e) => {
              commitNumericField(project.id, 'sellable_area', e.target.value, 'float');
              clearFieldEditValue(project.id, 'sellable_area');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                clearFieldEditValue(project.id, 'sellable_area');
                e.currentTarget.blur();
              }
            }}
            style={inlineInputStyle}
          />
        )}
      </td>
      
      {/* Срок реализации (мес) */}
      <td
        style={{ ...cellStyle(false, true, fixedColumnOffsets.duration, false, fixedColumnWidths.duration), fontSize: 11 }}
        title={!project.is_subtotal && !project.is_total ? `Автоматически: от «${CONSTRUCTION_DURATION_START_TYPE}» до «${CONSTRUCTION_DURATION_END_TYPE}»` : undefined}
      >
        {(() => {
          const duration = getProjectConstructionDuration(project);
          return duration ?? '—';
        })()}
      </td>

      {/* Перенос */}
      <td
        style={{
          ...cellStyle(false, true, fixedColumnOffsets.move, false, fixedColumnWidths.move),
          padding: '3px 4px'
        }}
      >
        {renderPoolMoveButton(project, pool)}
      </td>
      
      {/* Месяцы */}
      {filteredMonths.map((date, idx) => {
        const milestones = getMilestones(project, date) || [];
        const legendMilestones = milestones.filter(m => m.milestone_type);
        const primaryMilestone = legendMilestones[0] || null;
        const cellAreaValue = getCellAreaValue(project, date);
        const dateStr = formatDateLocal(date);
        const cellKey = `${project.id}-${dateStr}`;
        const isUpdating = updatingCells.has(cellKey);
        const isEditing = editingCell?.projectId === project.id && editingCell?.date === dateStr;
        const cellMilestoneStyle = legendMilestones.length === 1 ? getMilestoneCellStyle(primaryMilestone) : {};
        
        const isAreaEditing = editingAreaCell?.projectId === project.id && editingAreaCell?.date === dateStr;
        const inConstructionPeriod = isDateInConstructionPeriod(project, date);
        const periodEdges = getConstructionPeriodEdgeFlags(project, date);
        const constructionPeriodBorderStyle = periodEdges
          ? {
              boxShadow: [
                `inset 0 2px 0 0 ${constructionBorder}`,
                `inset 0 -2px 0 0 ${constructionBorder}`,
                periodEdges.isStart ? `inset 2px 0 0 0 ${constructionBorder}` : null,
                periodEdges.isEnd ? `inset -2px 0 0 0 ${constructionBorder}` : null,
              ].filter(Boolean).join(', '),
            }
          : {};

        return (
          <React.Fragment key={idx}>
            <td 
              data-testid={`strategic-map-cell-${project.id}-${dateStr}`}
              style={{ 
                ...cellStyle(),
                ...(!inConstructionPeriod && legendMilestones.length === 1 ? cellMilestoneStyle : {}),
                ...(inConstructionPeriod
                  ? { background: isDark ? CONSTRUCTION_PERIOD_BG.dark : CONSTRUCTION_PERIOD_BG.light }
                  : {}),
                ...constructionPeriodBorderStyle,
                cursor: project.is_subtotal || project.is_total || isUpdating || !strategicMapCanEdit ? 'default' : 'pointer',
                position: 'relative',
                padding: 2,
                transition: 'background 0.2s',
                opacity: isUpdating ? 0.6 : 1
              }}
              onClick={() => !project.is_subtotal && !project.is_total && handleCellClick(project.id, date)}
            >
              {isUpdating && (
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 5
                }}>
                  <div className="loading-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                </div>
              )}
              {!isEditing && (
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 52,
                  padding: '4px 2px'
                }}>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    flexGrow: 1,
                    justifyContent: 'center'
                  }}>
                  {legendMilestones.length === 0 ? (
                      <div style={{ 
                        fontSize: 9, 
                        fontWeight: 400,
                        color: 'var(--text-muted)',
                        lineHeight: 1.2,
                        minHeight: 12
                      }} />
                    ) : (
                      legendMilestones.map((m, idx) => {
                        const type = MILESTONE_TYPES[normalizeMilestoneType(m.milestone_type)] || { color: 'var(--text-primary)', bgColor: 'transparent' };
                        return (
                          <div
                            key={m.id || idx}
                            style={{
                              display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 4,
                            padding: '2px 4px',
                              borderRadius: 4,
                              background: type.bgColor,
                              color: type.color,
                              lineHeight: 1.2
                            }}
                          onClick={(e) => e.stopPropagation()}
                          >
                          <div style={{ fontSize: 9, fontWeight: 600, flex: 1, textAlign: 'left' }}>
                              {normalizeMilestoneType(m.milestone_type) || ''}
                            </div>
                          <button
                            type="button"
                            title="Удалить"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (project.is_subtotal || project.is_total || !strategicMapCanEdit) return;
                              deleteLegendMilestone(project, date, m);
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: type.color,
                              fontSize: 10,
                              lineHeight: 1,
                              cursor: strategicMapCanEdit ? 'pointer' : 'default',
                              padding: 0,
                              opacity: strategicMapCanEdit ? 0.7 : 0.3,
                              display: strategicMapCanEdit ? 'block' : 'none',
                            }}
                          >
                            ✕
                          </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (project.is_subtotal || project.is_total || !strategicMapCanEdit) return;
                      setEditingAreaCell({ projectId: project.id, date: dateStr, displayDate: date });
                      setAreaEditValue(cellAreaValue ?? '');
                    }}
                    style={{ 
                      fontSize: 8, 
                      fontWeight: cellAreaValue ? 600 : 400,
                      color: cellAreaValue ? 'var(--text-primary)' : 'var(--text-muted)',
                      lineHeight: 1.2,
                      minHeight: 18,
                      paddingTop: 2,
                      borderTop: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`,
                      textAlign: 'center',
                      cursor: project.is_subtotal || project.is_total || !strategicMapCanEdit ? 'default' : 'text'
                    }}
                  >
                    {isAreaEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={areaEditValue}
                        onChange={(e) => setAreaEditValue(e.target.value)}
                        onBlur={saveAreaOnlyEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveAreaOnlyEdit();
                          if (e.key === 'Escape') closeAreaEdit();
                        }}
                        autoFocus
                        style={{
                          width: '100%',
                          height: 18,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          color: 'inherit',
                          fontSize: 8,
                          textAlign: 'center'
                        }}
                      />
                    ) : (
                      cellAreaValue ? `${cellAreaValue.toLocaleString('ru-RU')} м²` : ''
                    )}
                  </div>
                </div>
              )}
            </td>
            {date.getMonth() === 11 && (
              <td style={{ ...yearTotalColumnStyle(false, false) }}>
                {(() => {
                  const total = getYearAreaTotal(project, date.getFullYear());
                  return total ? total.toLocaleString('ru-RU') : '—';
                })()}
              </td>
            )}
          </React.Fragment>
        );
      })}
      
    </tr>
  );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="loading-spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 data-testid="strategic-map-title" style={titleStyle}>Мастер-карта стратегического развития</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
            Планирование и контроль этапов строительных проектов
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={toolbarStyle}>
        {/* Первая строка: Поиск и фильтры */}
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          width: '100%'
        }}>
          <input
            type="text"
            placeholder="🔍 Поиск проекта..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ minWidth: 200, maxWidth: 300, flex: '1 1 200px' }}
          />
          
          <select 
            value={yearFilter} 
            onChange={(e) => setYearFilter(e.target.value)}
            style={{ minWidth: 120, flex: '0 0 auto' }}
          >
            <option value="all">Все годы</option>
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <ProjectOfficeFilterMultiSelect
            label="Статус"
            options={STATUS_OPTIONS}
            selected={selectedStatuses}
            onChange={setSelectedStatuses}
            emptyLabel="Все"
            minWidth={180}
            testIdPrefix="strategic-map-status-filter"
          />

          <div style={{ flex: 1, minWidth: 20 }} />

          {/* Управление годами */}
          <div style={yearRangeStyle}>
          <button
            onClick={addYearBefore}
            style={yearStepButtonStyle}
            title="Добавить год слева"
          >
            − год
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 240 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center' }}>
              {startYear} — {endYear}
            </div>
            
            {/* Начальный год */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'left' }}>
                Начало: {startYear}
              </label>
              <input
                type="range"
                min={yearRangeLimits.min}
                max={yearRangeLimits.max}
                value={startYear}
                onChange={(e) => {
                  const next = Math.min(parseInt(e.target.value, 10), endYear);
                  setStartYear(next);
                }}
                style={{
                  width: '100%',
                  height: 6,
                  borderRadius: 999,
                  appearance: 'none',
                  background: `linear-gradient(to right, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.4)'} 0%, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.4)'} ${((startYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} ${((startYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} 100%)`,
                  cursor: 'pointer'
                }}
              />
            </div>
            
            {/* Конечный год */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'left' }}>
                Конец: {endYear}
              </label>
              <input
                type="range"
                min={yearRangeLimits.min}
                max={yearRangeLimits.max}
                value={endYear}
                onChange={(e) => {
                  const next = Math.max(parseInt(e.target.value, 10), startYear);
                  setEndYear(next);
                }}
                style={{
                  width: '100%',
                  height: 6,
                  borderRadius: 999,
                  appearance: 'none',
                  background: `linear-gradient(to right, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} 0%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} ${((endYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.4)'} ${((endYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.4)'} 100%)`,
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
          <button
            onClick={addYearAfter}
            style={yearStepButtonStyle}
            title="Добавить год справа"
          >
            + год
          </button>
        </div>
        </div>

        {/* Вторая строка: Быстрый ввод */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8,
          width: '100%',
          flexWrap: 'wrap',
          paddingTop: 12,
          borderTop: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(203, 213, 225, 0.5)'}`
        }}>
          {strategicMapCanEdit && (
          <>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Быстрый ввод:</span>
          <div style={{ 
            display: 'flex', 
            gap: 6, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {Object.entries(MILESTONE_TYPES).map(([type, val]) => (
              <button
                key={type}
                onClick={() => setQuickFillType(quickFillType === type ? null : type)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: quickFillType === type 
                    ? val.color
                    : (isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(248, 250, 252, 0.8)'),
                  border: quickFillType === type 
                    ? `2px solid ${val.color}` 
                    : `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(203, 213, 225, 0.5)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: quickFillType === type 
                    ? `0 0 12px ${val.color}40` 
                    : 'none',
                  color: quickFillType === type 
                    ? '#fff'
                    : 'var(--text-primary)',
                  fontSize: 11,
                  fontWeight: quickFillType === type ? 600 : 500,
                  whiteSpace: 'nowrap'
                }}
                title={`Режим быстрой расстановки: ${type}`}
              >
                <div style={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: 2,
                  background: quickFillType === type ? '#fff' : val.color,
                  border: quickFillType === type ? 'none' : `2px solid ${val.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {quickFillType === type && (
                    <span style={{ fontSize: 8, color: val.color, fontWeight: 'bold' }}>✓</span>
                  )}
                </div>
                <span>{type}</span>
              </button>
            ))}
            {quickFillType && (
              <button
                onClick={() => setQuickFillType(null)}
                style={{ 
                  background: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)', 
                  border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.3)'}`,
                  borderRadius: 6,
                  cursor: 'pointer', 
                  fontSize: 11, 
                  padding: '6px 10px',
                  color: '#d4a0a0',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                title="Отключить быстрый ввод"
              >
                ✕ Отключить
              </button>
            )}
          </div>
          </>
          )}
        </div>
      </div>


      {/* Main Table */}
      <div ref={stickyMeasureRef} style={{ width: '100%', minWidth: 0 }}>
        <SyncedHorizontalScroll
          data-testid="strategic-map-table-scroll"
          style={tableContainerStyle}
          maxHeight="700px"
          onMainRef={tableRef}
          contentKey={`${mainPoolProjects.length}-${perspectivePoolProjects.length}-${filteredMonths.length}-${fixedColumnTotalWidth}`}
        >
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%', tableLayout: 'auto' }}>
          <thead>
            {/* Кварталы */}
            <tr>
              <th style={{ 
                ...cellStyle(true, true, fixedColumnOffsets.index, 'top', fixedColumnWidths.index),
                minHeight: headerRowHeights.top
              }}>
                №
              </th>
              <th style={{ 
                ...cellStyle(true, true, fixedColumnOffsets.project, 'top', fixedColumnWidths.project),
                minHeight: headerRowHeights.top
              }}>
                Проект
              </th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.status, 'top', fixedColumnWidths.status), minHeight: headerRowHeights.top }}>Текущий статус</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.sections, 'top', fixedColumnWidths.sections), minHeight: headerRowHeights.top }}>Кол-во секций</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.area, 'top', fixedColumnWidths.area), minHeight: headerRowHeights.top }}>Продаваемая площадь (М2)</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.duration, 'top', fixedColumnWidths.duration), minHeight: headerRowHeights.top }}>Срок реализации (мес)</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.move, 'top', fixedColumnWidths.move), minHeight: headerRowHeights.top, fontSize: 10 }}>Перенос</th>
              {Object.entries(quarters).map(([quarter, monthsInQuarter]) => {
                const quarterYear = monthsInQuarter[0]?.getFullYear();
                const quarterIndex = Math.floor(monthsInQuarter[0]?.getMonth() / 3) + 1;
                return (
                  <React.Fragment key={quarter}>
                    <th 
                      colSpan={monthsInQuarter.length}
                      style={{ 
                        ...cellStyle(true, false, 0, 'top'),
                        background: isDark ? 'rgba(51, 65, 85, 0.95)' : 'rgba(226, 232, 240, 0.98)',
                        fontSize: 22,
                        fontWeight: 700,
                      }}
                    >
                      {quarter}
                    </th>
                    {quarterIndex === 4 && (
                      <th style={{ ...yearTotalColumnStyle(true, 'top') }}>
                        Итого {quarterYear}
                      </th>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
            
            {/* Месяцы - первая строка (статусы) */}
            <tr>
              <th style={{ 
                ...cellStyle(true, true, fixedColumnOffsets.index, 'top2', fixedColumnWidths.index),
                fontSize: 9,
                color: 'var(--text-muted)'
              }}>
                №
              </th>
              <th style={{ 
                ...cellStyle(true, true, fixedColumnOffsets.project, 'top2', fixedColumnWidths.project),
                fontSize: 9,
                color: 'var(--text-muted)'
              }}>
                название объекта
              </th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.status, 'top2', fixedColumnWidths.status), fontSize: 9 }}>статус</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.sections, 'top2', fixedColumnWidths.sections), fontSize: 9 }}>секций</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.area, 'top2', fixedColumnWidths.area), fontSize: 9 }}>площадь м²</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.duration, 'top2', fixedColumnWidths.duration), fontSize: 9 }}>срок мес.</th>
              <th style={{ ...cellStyle(true, true, fixedColumnOffsets.move, 'top2', fixedColumnWidths.move), fontSize: 8 }}>участки</th>
              {filteredMonths.map((date, idx) => (
                <React.Fragment key={idx}>
                  <th 
                    style={{ 
                      ...cellStyle(true, false, 0, 'top2'),
                      fontSize: 10,
                      padding: '4px 2px',
                      minHeight: headerRowHeights.top2,
                      lineHeight: 1.2,
                      color: date.getMonth() === 0 ? 'var(--text-primary)' : 'var(--text-muted)',
                      fontWeight: date.getMonth() === 0 ? 700 : 400,
                      background: date.getMonth() === 0 
                        ? (isDark ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.2)')
                        : (isDark ? '#1e293b' : '#f8fafc')
                    }}
                  >
                    {formatMonth(date)}
                    {date.getMonth() === 0 && <div style={{ fontSize: 8, lineHeight: 1.2 }}>{date.getFullYear()}</div>}
                  </th>
                  {date.getMonth() === 11 && (
                    <th style={{ ...yearTotalColumnStyle(true, 'top2') }}>
                      итого
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
            
          </thead>
          
          <tbody>
            {mainPoolProjects.length === 0 ? (
              <tr>
                <td colSpan={tableColSpan} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  {searchText ? 'Проекты не найдены' : 'Проекты автоматически загружаются из объектов строительства...'}
                </td>
              </tr>
            ) : (
              mainPoolProjects.map((project, rowIdx) => renderProjectRow(project, rowIdx, 'main'))
            )}
            
            {/* Итоговые строки */}
            {mainPoolProjects.length > 0 && (
              <>
                {/* Итого в строительстве м² по месяцам */}
                <tr style={{ 
                  background: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                  borderTop: `2px solid ${isDark ? 'rgba(245, 158, 11, 0.4)' : 'rgba(245, 158, 11, 0.3)'}`
                }}>
                  <td 
                    colSpan={7}
                    style={{ 
                      ...cellStyle(false, true, fixedColumnOffsets.index, false, fixedColumnTotalWidth),
                      fontWeight: 700,
                      fontSize: 12,
                      textAlign: 'left',
                      padding: '10px 12px',
                      background: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
                      color: isDark ? '#fbbf24' : '#d97706'
                    }}
                  >
                    Итого в строительстве м² по месяцам
                  </td>
                  {filteredMonths.map((date, idx) => {
                    const totalArea = constructionProjects.reduce((sum, project) => {
                      const milestones = getMilestones(project, date) || [];
                      const areaValue = milestones.find(m => m.area_value !== null && m.area_value !== undefined)?.area_value || 0;
                      return sum + areaValue;
                    }, 0);
                    
                    return (
                      <React.Fragment key={idx}>
                        <td style={{ 
                          ...cellStyle(false, false),
                          fontWeight: 700,
                          fontSize: 11,
                          background: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                          color: totalArea > 0 ? (isDark ? '#fbbf24' : '#d97706') : 'var(--text-muted)'
                        }}>
                          {totalArea > 0 ? totalArea.toLocaleString('ru-RU') : '—'}
                        </td>
                        {date.getMonth() === 11 && (
                          <td style={{ 
                            ...yearTotalColumnStyle(false, false),
                            fontWeight: 700,
                            fontSize: 11,
                            background: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                            color: isDark ? '#fbbf24' : '#d97706'
                          }}>
                            {(() => {
                              const yearTotal = constructionProjects.reduce((sum, project) => {
                                const total = getYearAreaTotal(project, date.getFullYear());
                                return sum + (total || 0);
                              }, 0);
                              return yearTotal > 0 ? yearTotal.toLocaleString('ru-RU') : '—';
                            })()}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>

                {/* Итого в строительстве объектов */}
                <tr style={{ 
                  background: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
                }}>
                  <td 
                    colSpan={7}
                    style={{ 
                      ...cellStyle(false, true, fixedColumnOffsets.index, false, fixedColumnTotalWidth),
                      fontWeight: 700,
                      fontSize: 12,
                      textAlign: 'left',
                      padding: '10px 12px',
                      background: isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                      color: isDark ? '#fbbf24' : '#d97706'
                    }}
                  >
                    Итого в строительстве объектов
                  </td>
                  {filteredMonths.map((date, idx) => {
                    const count = constructionProjects.filter(project => {
                      const milestones = getMilestones(project, date) || [];
                      return milestones.some(m => m.milestone_type);
                    }).length;
                    
                    return (
                      <React.Fragment key={idx}>
                        <td style={{ 
                          ...cellStyle(false, false),
                          fontWeight: 700,
                          fontSize: 11,
                          background: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
                          color: count > 0 ? (isDark ? '#fbbf24' : '#d97706') : 'var(--text-muted)'
                        }}>
                          {count > 0 ? count : '—'}
                        </td>
                        {date.getMonth() === 11 && (
                          <td style={{ 
                            ...yearTotalColumnStyle(false, false),
                            fontWeight: 700,
                            fontSize: 11,
                            background: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
                            color: isDark ? '#fbbf24' : '#d97706'
                          }}>
                            {(() => {
                              const yearCount = constructionProjects.filter(project => {
                                const yearDates = filteredMonths.filter(d => d.getFullYear() === date.getFullYear());
                                return yearDates.some(d => {
                                  const milestones = getMilestones(project, d) || [];
                                  return milestones.some(m => m.milestone_type);
                                });
                              }).length;
                              return yearCount > 0 ? yearCount : '—';
                            })()}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              </>
            )}

            <tr data-testid="strategic-map-perspective-header">
              <td
                colSpan={tableColSpan}
                style={{
                  padding: '12px 14px',
                  fontSize: 14,
                  fontWeight: 700,
                  textAlign: 'left',
                  background: isDark ? 'rgba(96, 165, 250, 0.12)' : 'rgba(59, 130, 246, 0.08)',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  borderTop: `2px solid ${isDark ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.25)'}`,
                }}
              >
                Перспективные участки
              </td>
            </tr>
            {perspectivePoolProjects.length === 0 ? (
              <tr>
                <td colSpan={tableColSpan} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Нет перспективных участков
                </td>
              </tr>
            ) : (
              perspectivePoolProjects.map((project, rowIdx) => renderProjectRow(project, rowIdx, 'perspective'))
            )}
          </tbody>
        </table>
        </SyncedHorizontalScroll>
      </div>

      {/* Summary Table - Area by Status */}
      <div
        data-testid="strategic-map-totals"
        style={{ 
        marginTop: 24,
        background: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.95)',
        borderRadius: 16,
        border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.4)' : 'rgba(203, 213, 225, 0.6)'}`,
        boxShadow: isDark 
          ? '0 4px 24px rgba(0, 0, 0, 0.3)' 
          : '0 4px 24px rgba(0, 0, 0, 0.08)',
        padding: 16,
        maxWidth: 600
      }}>
        <h3 style={{ 
          fontSize: 16, 
          fontWeight: 600, 
          marginBottom: 12,
          color: 'var(--text-primary)'
        }}>
          ИТОГО
        </h3>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse'
        }}>
          <tbody>
            {summaryTableRows.map((row) => {
              const total = (row.type === 'editable' || row.type === 'computed')
                ? row.value
                : summaryTotals.byStatus[row.status];
              const bgColor = getSummaryRowBgColor(row.label);
              const summaryFieldKey = getStrategySummaryFieldKey(row.key);
              const summaryEditValue = projectFieldEdits[summaryFieldKey];

              return (
                <tr key={row.key} data-testid={getSummaryRowTestId(row)} style={{ 
                  borderBottom: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`
                }}>
                  <td style={{ 
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    background: bgColor,
                    borderRight: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.3)' : 'rgba(226, 232, 240, 0.8)'}`
                  }}>
                    {row.label}
                  </td>
                  <td style={{ 
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: 'right',
                    color: 'var(--text-primary)',
                    background: bgColor
                  }}>
                    {row.type === 'editable' && strategicMapCanEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        data-testid="strategic-map-total-strategy-2027-input"
                        value={summaryEditValue ?? getDisplayValue(total === 0 ? '' : total)}
                        onChange={(e) => setSummaryEditValue(row.key, e.target.value)}
                        onBlur={(e) => {
                          commitStrategySummaryValue(row, e.target.value);
                          clearSummaryEditValue(row.key);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') {
                            clearSummaryEditValue(row.key);
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder="Введите значение"
                        style={{
                          width: '100%',
                          height: 32,
                          border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(203, 213, 225, 0.9)'}`,
                          borderRadius: 8,
                          padding: '0 10px',
                          textAlign: 'right',
                          background: isDark ? 'rgba(15, 23, 42, 0.8)' : '#fff',
                          color: 'var(--text-primary)',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      formatSummaryDisplayValue(row, total)
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Window */}
      {editingCell && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="strategic-map-cell-dialog"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 'var(--z-modal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeEditModal();
          }}
        >
          <div
            style={{
              background: isDark ? '#1e293b' : '#fff',
              padding: 20,
              borderRadius: 12,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              minWidth: 320,
              border: `2px solid ${isDark ? '#475569' : '#e2e8f0'}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {editingCell && projects.find(p => p.id === editingCell.projectId)?.name.trim()}
            </div>
            <div style={{ marginBottom: 16, fontSize: 11, color: 'var(--text-muted)' }}>
              {editingCell && formatMonth(editingCell.displayDate || new Date(editingCell.date))} {editingCell && (editingCell.displayDate || new Date(editingCell.date)).getFullYear()}
            </div>
            <select
              data-testid="strategic-map-cell-milestone-type"
              value={selectedMilestoneType}
              onChange={(e) => setSelectedMilestoneType(e.target.value)}
              style={{ marginBottom: 12, fontSize: 13, height: 40, width: '100%' }}
            >
              <option value="">— Выберите тип вехи —</option>
              {Object.keys(MILESTONE_TYPES).map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              type="text"
              data-testid="strategic-map-cell-milestone-value"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Комментарий / значение вехи"
              style={{
                marginBottom: 12,
                fontSize: 13,
                height: 40,
                width: '100%',
                border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
                borderRadius: 8,
                padding: '0 12px',
                background: isDark ? 'rgba(15, 23, 42, 0.8)' : '#fff',
                color: 'var(--text-primary)',
              }}
            />
            <input
              type="number"
              step="0.01"
              data-testid="strategic-map-cell-milestone-area"
              value={editAreaValue}
              onChange={(e) => setEditAreaValue(e.target.value)}
              placeholder="Площадь, м²"
              style={{
                marginBottom: 16,
                fontSize: 13,
                height: 40,
                width: '100%',
                border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
                borderRadius: 8,
                padding: '0 12px',
                background: isDark ? 'rgba(15, 23, 42, 0.8)' : '#fff',
                color: 'var(--text-primary)',
              }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button 
                data-testid="strategic-map-cell-save"
                onClick={saveCellEdit} 
                style={{ 
                  flex: 2, 
                  height: 40,
                  background: '#7b9eb8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#5a7d96'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#7b9eb8'}
              >
                Сохранить
              </button>
              <button 
                onClick={closeEditModal} 
                style={{ 
                  flex: 1, 
                  height: 40,
                  background: isDark ? '#475569' : '#e2e8f0',
                  color: isDark ? '#fff' : '#1e293b',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? '#64748b' : '#cbd5e1'}
                onMouseLeave={(e) => e.currentTarget.style.background = isDark ? '#475569' : '#e2e8f0'}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StrategicMap;
